/*
  Launch Remedy — cart drawer controller
  ---------------------------------------------------------------------------
  Defines the <cart-drawer> custom element that sections/cart-drawer.liquid
  renders and that sections/main-product.liquid already expects.

  main-product.liquid (unchanged) does the ajax add itself and then calls:
      cartDrawer.getSectionsToRender()   -> section names to ask /cart/add.js for
      cartDrawer.renderContents(data)    -> swap in returned HTML + open
      cartDrawer.open()                  -> fallback when no sections came back
  Those three methods are the contract this file implements, so the setup-service
  logic (capsule + install/customize variant + "Service for" property) keeps
  working exactly as authored, with no change to that section.

  This file also owns, for the rest of the theme:
    - the header cart button -> opens the same drawer
    - a fallback ajax add-to-cart for any other /cart/add form
    - quantity + remove via /cart/change.js
    - [data-cart-count] refresh from the server response
    - open/close, Escape, overlay, focus trap, scroll lock
*/
(function () {
  'use strict';

  var SECTION = 'cart-drawer';
  var INNER = '[data-cart-drawer-inner]';
  var CLOSE_MS = 340;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  var IS_IOS =
    /iPad|iPhone|iPod/.test(window.navigator.userAgent) ||
    (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);

  function parseSectionHtml(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector(INNER);
  }

  function formatCount(n) {
    return '(' + n + ')';
  }

  class LRCartDrawer extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this._busy = false;

      this._onDocKeydown = this._onDocKeydown.bind(this);

      this.addEventListener('click', this._onClick.bind(this));
      this.addEventListener('change', this._onChange.bind(this));
      this.addEventListener('keydown', this._onPanelKeydown.bind(this));
      this.addEventListener('submit', this._onCheckoutSubmit.bind(this));
    }

    /* ----------------------------------------------------------- references */

    get panel() {
      return this.querySelector('[data-cart-drawer-panel]');
    }

    get inner() {
      return this.querySelector(INNER);
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    /* ------------------------------------------- API used by main-product.liquid */

    getSectionsToRender() {
      return [{ id: SECTION, section: SECTION, selector: INNER }];
    }

    renderContents(parsedState) {
      var html = parsedState && parsedState.sections && parsedState.sections[SECTION];

      if (html) {
        this.setInner(html);
        this.open();
        return;
      }

      // No usable section HTML came back: fetch it rather than opening an empty panel.
      var self = this;
      this.refresh()
        .then(function () {
          self.open();
        })
        .catch(function () {
          self.open();
          self.showError();
        });
    }

    /* ------------------------------------------------------------ rendering */

    setInner(html) {
      var incoming = typeof html === 'string' ? parseSectionHtml(html) : html;
      var current = this.inner;
      if (!incoming || !current) return false;

      current.replaceWith(incoming);
      this.syncCount();
      return true;
    }

    refresh() {
      var self = this;
      var url = root() + '?sections=' + SECTION;

      return fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (res) {
          if (!res.ok) throw new Error('Cart refresh failed (' + res.status + ')');
          return res.json();
        })
        .then(function (data) {
          if (data && data[SECTION]) self.setInner(data[SECTION]);
          return data;
        });
    }

    /* --------------------------------------------------------- cart counters */

    syncCount() {
      var el = this.inner;
      if (!el) return;
      var n = parseInt(el.getAttribute('data-cart-item-count'), 10);
      if (!isNaN(n)) this.updateCount(n);
    }

    updateCount(n) {
      document.querySelectorAll('[data-cart-count]').forEach(function (el) {
        el.textContent = formatCount(n);
      });

      document.querySelectorAll('a.lr-premium-cart[aria-label]').forEach(function (el) {
        el.setAttribute('aria-label', 'Cart with ' + n + ' item' + (n === 1 ? '' : 's'));
      });
    }

    /* ------------------------------------------------------------- open/close */

    open() {
      if (this.isOpen) return;

      this._returnFocus = document.activeElement;
      this.hidden = false;

      var self = this;

      /*
        Force a reflow so the browser records the closed state before the class
        lands; the transition then plays without depending on requestAnimationFrame.
        rAF never fires in a backgrounded tab, which previously could leave the
        panel off-screen while the page scroll was already locked.
      */
      void this.offsetWidth;
      this.classList.add('is-open');

      this._lockScroll();
      document.addEventListener('keydown', this._onDocKeydown);

      window.setTimeout(function () {
        var target = self.querySelector('[data-cart-drawer-close]') || self.panel;
        if (target) {
          try {
            target.focus({ preventScroll: true });
          } catch (e) {
            target.focus();
          }
        }
      }, reducedMotion() ? 0 : 80);

      this.dispatchEvent(new CustomEvent('cart-drawer:open', { bubbles: true }));
    }

    close() {
      // Also run when the class was cleared by something else but we still hold
      // the scroll lock, otherwise the page can be left unscrollable.
      if (!this.isOpen && !this._locked) return;

      this.classList.remove('is-open');
      document.removeEventListener('keydown', this._onDocKeydown);

      var self = this;
      var finish = function () {
        if (!self.classList.contains('is-open')) self.hidden = true;
        self._unlockScroll();

        var back = self._returnFocus;
        self._returnFocus = null;
        if (back && document.contains(back) && typeof back.focus === 'function') {
          try {
            back.focus({ preventScroll: true });
          } catch (e) {
            back.focus();
          }
        }
      };

      if (reducedMotion()) finish();
      else window.setTimeout(finish, CLOSE_MS);

      this.dispatchEvent(new CustomEvent('cart-drawer:close', { bubbles: true }));
    }

    /* -------------------------------------------------------- scroll locking */

    _lockScroll() {
      if (this._locked) return; // never capture an already-locked state as "previous"
      this._locked = true;

      var b = document.body;
      this._scrollY = window.scrollY || window.pageYOffset || 0;
      this._prevStyle = {
        overflow: b.style.overflow,
        paddingRight: b.style.paddingRight,
        position: b.style.position,
        top: b.style.top,
        width: b.style.width
      };

      var gutter = window.innerWidth - document.documentElement.clientWidth;
      if (gutter > 0) b.style.paddingRight = gutter + 'px';
      b.style.overflow = 'hidden';

      // iOS ignores overflow:hidden on body for touch scrolling.
      if (IS_IOS) {
        b.style.position = 'fixed';
        b.style.top = -this._scrollY + 'px';
        b.style.width = '100%';
      }
    }

    _unlockScroll() {
      if (!this._locked) return;
      this._locked = false;

      var b = document.body;
      var p = this._prevStyle;
      if (!p) return;

      b.style.overflow = p.overflow;
      b.style.paddingRight = p.paddingRight;
      b.style.position = p.position;
      b.style.top = p.top;
      b.style.width = p.width;
      this._prevStyle = null;

      if (IS_IOS) window.scrollTo(0, this._scrollY || 0);
    }

    /* --------------------------------------------------------------- errors */

    showError(message) {
      var el = this.querySelector('[data-cart-drawer-error]');
      if (!el) return;
      el.textContent = message || this.getAttribute('data-error-generic') || 'Something went wrong. Please try again.';
      el.hidden = false;
    }

    clearError() {
      var el = this.querySelector('[data-cart-drawer-error]');
      if (!el) return;
      el.textContent = '';
      el.hidden = true;
    }

    /* ----------------------------------------------------------- interaction */

    _onDocKeydown(event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.stopPropagation();
        this.close();
      }
    }

    _onPanelKeydown(event) {
      if (event.key !== 'Tab') return;

      var panel = this.panel;
      if (!panel) return;

      var nodes = Array.prototype.filter.call(
        panel.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ),
        function (el) {
          return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
        }
      );

      if (!nodes.length) {
        event.preventDefault();
        panel.focus();
        return;
      }

      var first = nodes[0];
      var last = nodes[nodes.length - 1];
      var active = document.activeElement;

      if (!panel.contains(active)) {
        event.preventDefault();
        first.focus();
        return;
      }

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    _onClick(event) {
      if (event.target.closest('[data-cart-drawer-scrim]')) {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.target.closest('[data-cart-drawer-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      var step = event.target.closest('[data-cart-qty-step]');
      if (step) {
        event.preventDefault();
        this._step(step);
        return;
      }

      var remove = event.target.closest('[data-cart-remove]');
      if (remove) {
        event.preventDefault();
        var item = remove.closest('[data-cart-line-item]');
        if (item) this._change(item.getAttribute('data-line-key'), 0, item);
      }
    }

    _onChange(event) {
      var input = event.target.closest('[data-cart-qty-input]');
      if (!input) return;

      var item = input.closest('[data-cart-line-item]');
      if (!item) return;

      var qty = parseInt(input.value, 10);
      if (isNaN(qty) || qty < 0) qty = 0;

      this._change(item.getAttribute('data-line-key'), qty, item);
    }

    _step(button) {
      var item = button.closest('[data-cart-line-item]');
      if (!item) return;

      var input = item.querySelector('[data-cart-qty-input]');
      var delta = parseInt(button.getAttribute('data-cart-qty-step'), 10) || 0;
      var current = parseInt(input && input.value, 10);
      if (isNaN(current)) current = 0;

      var next = current + delta;
      if (next < 0) next = 0;

      this._change(item.getAttribute('data-line-key'), next, item);
    }

    _change(key, quantity, itemEl) {
      if (!key || this._busy) return;

      this._busy = true;
      this.clearError();
      if (itemEl) itemEl.classList.add('is-busy');

      var self = this;
      var url = root() + 'cart/change.js';

      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: key,
          quantity: quantity,
          sections: [SECTION],
          sections_url: window.location.pathname
        })
      })
        .then(function (res) {
          return res.json().then(function (data) {
            if (!res.ok || data.status) {
              throw new Error(data.description || data.message || '');
            }
            return data;
          });
        })
        .then(function (data) {
          var swapped = data.sections && data.sections[SECTION] ? self.setInner(data.sections[SECTION]) : false;

          if (typeof data.item_count === 'number') self.updateCount(data.item_count);

          if (!swapped) return self.refresh();
          return null;
        })
        .catch(function (error) {
          if (itemEl) itemEl.classList.remove('is-busy');
          self.showError(error && error.message ? error.message : null);
        })
        .then(function () {
          self._busy = false;
        });
    }

    _onCheckoutSubmit(event) {
      var form = event.target.closest('[data-cart-drawer-checkout-form]');
      if (!form) return;

      var button = form.querySelector('[data-cart-drawer-checkout]');
      if (!button) return;

      if (form.dataset.lrSubmitting === '1') {
        event.preventDefault();
        return;
      }

      form.dataset.lrSubmitting = '1';
      button.setAttribute('aria-busy', 'true');

      // Let the native POST to /cart proceed; guard only against double clicks.
      window.setTimeout(function () {
        form.dataset.lrSubmitting = '';
        button.removeAttribute('aria-busy');
      }, 6000);
    }
  }

  if (!window.customElements.get('cart-drawer')) {
    window.customElements.define('cart-drawer', LRCartDrawer);
  }

  /* =========================================================================
     Theme-wide wiring
     ========================================================================= */

  function drawer() {
    return document.querySelector('cart-drawer');
  }

  /* --- Header cart button ------------------------------------------------- */

  document.addEventListener('click', function (event) {
    if (event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    var trigger = event.target.closest('[data-cart-drawer-toggle], a[href$="/cart"], a[href*="/cart?"]');
    if (!trigger) return;

    // Never hijack links inside the drawer, opt-outs, new-tab links, or the cart page itself.
    if (trigger.closest('cart-drawer')) return;
    if (trigger.closest('[data-no-cart-drawer]') || trigger.hasAttribute('data-no-cart-drawer')) return;
    if (trigger.target && trigger.target !== '' && trigger.target !== '_self') return;
    if (document.body.classList.contains('template-cart')) return;

    var d = drawer();
    if (!d) return;

    event.preventDefault();
    d.clearError();

    d.refresh()
      .then(function () {
        d.open();
      })
      .catch(function () {
        d.open();
        d.showError();
      });
  });

  /* --- Fallback ajax add-to-cart ------------------------------------------
     sections/main-product.liquid handles its own form and calls
     stopImmediatePropagation(), so this listener never double-fires there.
     It covers any other /cart/add form in the theme.
  ------------------------------------------------------------------------- */

  function inlineError(form, message) {
    var el = form.querySelector('[data-lr-cart-error]');

    if (!el) {
      el = document.createElement('p');
      el.setAttribute('data-lr-cart-error', '');
      el.setAttribute('role', 'alert');
      el.style.cssText =
        'margin:10px 0 0;color:#8a3310;font-size:13px;line-height:1.4;font-family:inherit;';
      var anchor = form.querySelector('[type="submit"]');
      if (anchor && anchor.parentNode) anchor.parentNode.appendChild(el);
      else form.appendChild(el);
    }

    el.textContent = message;
    el.hidden = false;
  }

  function clearInlineError(form) {
    var el = form.querySelector('[data-lr-cart-error]');
    if (el) {
      el.textContent = '';
      el.hidden = true;
    }
  }

  document.addEventListener('submit', function (event) {
    if (event.defaultPrevented) return;

    var form = event.target;
    if (!form || !form.matches) return;

    var action = form.getAttribute('action') || '';
    var isAddForm = action.indexOf('/cart/add') !== -1 || form.dataset.type === 'add-to-cart-form';
    if (!isAddForm) return;

    var d = drawer();
    if (!d) return; // no drawer: let Shopify's native POST happen rather than swallowing the add

    event.preventDefault();

    if (form.dataset.lrCartBusy === '1') return; // duplicate submission guard
    form.dataset.lrCartBusy = '1';
    clearInlineError(form);

    var button = form.querySelector('[type="submit"]');
    var restore = function () {
      form.dataset.lrCartBusy = '';
      if (button) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    };

    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }

    var body = new FormData(form);
    body.append('sections', SECTION);
    body.append('sections_url', window.location.pathname);

    fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: body
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok || data.status) {
            throw new Error(data.description || data.message || '');
          }
          return data;
        });
      })
      .then(function (data) {
        d.clearError();
        if (data.sections && data.sections[SECTION]) d.setInner(data.sections[SECTION]);
        else return d.refresh();
        return null;
      })
      .then(function () {
        d.open();
      })
      .catch(function (error) {
        inlineError(
          form,
          (error && error.message) ||
            d.getAttribute('data-error-generic') ||
            'Something went wrong. Please try again.'
        );
      })
      .then(restore);
  });

  /* --- Theme editor: re-sync after a section reload ----------------------- */

  document.addEventListener('shopify:section:load', function (event) {
    if (event.detail && event.detail.sectionId && drawer()) drawer().syncCount();
  });
})();
